import { showAlertCallout } from "@/shared/alerts";
import { normalizeHttpError } from "@/shared/utils/errors/http-error.factory";
import { isFileTooLargeError } from "@/shared/utils/errors/http/mathers/is-file-too-large-error";
import { isUnsupportedContentTypeError } from "@/shared/utils/errors/http/mathers/is-unsupported-content-type-error";
import { computeFileMD5 } from "@/shared/utils/file";
import { formatBytes } from "@spz/ui/hooks/use-file-upload";
import axios from "axios";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export type GetSignedUrlFnParams = {
  file: File;
  data: {
    md5Hash: string;
    fileName: string;
  };
};
export type GetSignedUrlFn = (params: GetSignedUrlFnParams) => Promise<{
  uploadUrl: string;
  id: string;
}>;

const uploadFile = async ({
  file,
  getSignedUrl,
  onSuccess,
  onError,
}: {
  file: File;
  getSignedUrl: GetSignedUrlFn;
  onSuccess?: (result: { id: string }) => void;
  onError?: (error?: unknown) => void;
}): Promise<{ id: string } | undefined> => {
  try {
    const md5Hash = await computeFileMD5(file);

    const { uploadUrl, id } = await getSignedUrl({
      file,
      data: {
        md5Hash,
        fileName: encodeURIComponent(file.name),
      },
    });
    const base64Md5Hash = await computeFileMD5(file, "base64");

    const headers = {
      "Content-MD5": base64Md5Hash,
      "Content-Type": file.type || "application/octet-stream",
      "x-goog-meta-original-filename": encodeURIComponent(file.name),
    };

    await axios.put(uploadUrl, file, {
      headers,
    });

    const result = { id };
    onSuccess?.(result);

    return result;
  } catch (error) {
    onError?.(error);
  }
};

export const useUploadFileMutation = ({
  maxFileSize,
  getSignedUrl,
}: {
  maxFileSize: number;
  getSignedUrl: GetSignedUrlFn;
}) => {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(
    async ({ file }: { file: File }) => {
      setIsPending(true);

      const result = await uploadFile({
        file,
        getSignedUrl,
        onSuccess: () => {
          // Not implemented
        },
        onError: (error: unknown) => {
          const httpError = normalizeHttpError(error);

          if (isFileTooLargeError(httpError)) {
            showAlertCallout({
              type: "critical",
              title: t("max_file_size", {
                size: formatBytes(maxFileSize),
              }),
            });
          }

          if (isUnsupportedContentTypeError(httpError)) {
            showAlertCallout({
              type: "critical",
              title: t("attachment_invalid_format"),
            });
          }

          showAlertCallout({
            type: "critical",
            title: t("generic_error"),
          });
        },
      });

      setIsPending(false);

      return result;
    },
    [t, maxFileSize, getSignedUrl],
  );

  return {
    isPending,
    mutateAsync,
  };
};
